import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import type { IndicesArray } from '@babylonjs/core/types';
import { firstValueFrom, Observable, Subject } from 'rxjs';

import { isFeatureNode, type Feature } from '@kb3d/models/classes/features/feature';
import type { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { Token } from '@kb3d/models/classes/token';
import type {
    IWorkerRenderFeaturesDataPayload,
    IWorkerRenderFeaturesPayload,
    IWorkerRenderUpdateGeometryError,
} from '@kb3d/models/interfaces/worker-types';
import { ISubmeshVertexData, SubmeshVertexData, verifyGeometry } from '@kb3d/view/helpers/geometry-helper';

import { isCustomMeshNode, isMeshNode } from '@kb3d/models/classes/meshes/mesh-utilities';
import { isTrackedClass, ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import type { KbViewer } from '../kb-viewer';
import { Kb3dWorker } from '../kb-worker';
import { calculateBaseSubmeshes, FeatureOrderManager } from './feature-stack-common';
import { AmbientOcclusionRenderer } from './features/ambient-occlusion';
import { CircularPatternRenderer } from './features/circular-pattern';
import { DeleteFacesRenderer } from './features/delete-faces';
import { ChamferFeatureRenderer } from './features/chamfer';
import { DisplacementMapRenderer } from './features/displacement-map';
import { ExtrudeRenderer } from './features/extrude';
import { FeatureRenderer, SubmeshVertexDataMap } from './features/feature';
import { JoinGeometryRenderer } from './features/join-geometry';
import { LinearPatternRenderer } from './features/linear-pattern';
import { MirrorFeatureRenderer } from './features/mirror';
import { MoveVerticesRenderer } from './features/move-vertices';
import { NormalSmoothingRenderer } from './features/normal-smoothing';
import { SliceFeatureRenderer } from './features/slice';
import { SubdivisionFeatureRenderer } from './features/subdivision';
import { SubmeshRenderer } from './features/submesh';
import { SweepRenderer } from './features/sweep';
import { TransformRenderer } from './features/transform';
import { UvMapRenderer } from './features/uvmap';
import { WeldVerticesRenderer } from './features/weld-vertices';
import { Renderer } from './renderer';
import type { RendererManager } from './renderer-manager';
import { MergePolygonsRenderer } from './features/merge-polygons';


export type MeshUpdateType = 'featureOrder' | 'geometry' | 'all';

export class FeatureStackWorkerRenderer extends Renderer<Feature, Mesh> {
    modelToken = Token.Feature;
    renderers = new Map<Token, FeatureRenderer<any>>();
    debugMode = false;
    busy: Observable<boolean>;

    protected featureOrder = new FeatureOrderManager();

    get workersBusy() {
        return this._workersBusy;
    }
    protected _busy = new Subject<boolean>();
    protected _workersBusy = false;
    protected worker: Kb3dWorker;
    protected updateQueue: Map<string, MeshNode> = new Map();
    /* 
        [dynamicId]: SubmeshVertexData
        This map contains geometry that has been processed by the worker, so what is being passed back to the main thread
    */
    protected baseGeometryMap: SubmeshVertexDataMap = new Map<string, SubmeshVertexData>();
    protected processedGeometryMap: SubmeshVertexDataMap = new Map<string, SubmeshVertexData>();

    constructor(viewer: KbViewer, scene: Scene, private rendererManager: RendererManager) {
        super(viewer, scene);
        this.worker = new Kb3dWorker();
        this.busy = this._busy.asObservable();

        const moveVertices = new MoveVerticesRenderer(viewer, scene);
        this.renderers.set(Token.MoveVerticesFeature, moveVertices);
        const weldVertices = new WeldVerticesRenderer(viewer, scene);
        this.renderers.set(Token.WeldVerticesFeature, weldVertices);
        const displacementMap = new DisplacementMapRenderer(viewer, scene);
        this.renderers.set(Token.DisplacementMapFeature, displacementMap);
        const uvMaps = new UvMapRenderer(viewer, scene);
        this.renderers.set(Token.UvMapFeature, uvMaps);
        const submeshes = new SubmeshRenderer(viewer, scene, rendererManager);
        this.renderers.set(Token.SubmeshFeature, submeshes);
        const linearPatterns = new LinearPatternRenderer(viewer, scene);
        this.renderers.set(Token.LinearPatternFeature, linearPatterns);
        const circularPatterns = new CircularPatternRenderer(viewer, scene);
        this.renderers.set(Token.CircularPatternFeature, circularPatterns);
        const joinGeometry = new JoinGeometryRenderer(viewer, scene);
        this.renderers.set(Token.JoinGeometryFeature, joinGeometry);
        const deleteFaces = new DeleteFacesRenderer(viewer, scene);
        this.renderers.set(Token.DeleteFacesFeature, deleteFaces);
        const mergePolygons = new MergePolygonsRenderer(viewer, scene);
        this.renderers.set(Token.MergePolygonsFeature, mergePolygons);
        const chamfer = new ChamferFeatureRenderer(viewer, scene);
        this.renderers.set(Token.ChamferFeature, chamfer);
        const normalSmoothing = new NormalSmoothingRenderer(viewer, scene);
        this.renderers.set(Token.NormalSmoothingFeature, normalSmoothing);
        const slice = new SliceFeatureRenderer(viewer, scene);
        this.renderers.set(Token.SliceFeature, slice);
        const subdivision = new SubdivisionFeatureRenderer(viewer, scene);
        this.renderers.set(Token.SubdivisionFeature, subdivision);
        const mirror = new MirrorFeatureRenderer(viewer, scene);
        this.renderers.set(Token.MirrorFeature, mirror);
        const transform = new TransformRenderer(viewer, scene);
        this.renderers.set(Token.TransformFeature, transform);
        const extrude = new ExtrudeRenderer(viewer, scene);
        this.renderers.set(Token.ExtrudeFeature, extrude);
        const sweep = new SweepRenderer(viewer, scene);
        this.renderers.set(Token.SweepFeature, sweep);
        const ambientOcclusion = new AmbientOcclusionRenderer(viewer, scene);
        this.renderers.set(Token.AmbientOcclusionFeature, ambientOcclusion);
    }

    protected update3dInternal(feature: ITrackedClass<Feature>): void {
        if (this.debugMode) {
            return;
        }
        if (isTrackedClass(feature)) {
            if (!feature.enabled || feature._suppressed) {
                if (feature.changes.has('enabled') || feature.changes.has('_suppressed')) {
                    this.delete3d(feature, true);
                }
                return;
            }
            // Process all of the feature's non-geometry effects
            const renderer = this.renderers.get(Token.withKey(feature.$type));
            if (!renderer) {
                throw Error('no renderer for feature');
            }
            renderer.update3d(feature);
            if (feature._parent) {
                this.queueMeshForUpdate(feature._parent);
            }
        }
    }

    isStable() {
        return !this._workersBusy && this.updateQueue.size === 0;
    }

    /** Possibly in the future we can pass in what changed (transform, etc) */
    queueMeshForUpdate(node: MeshNode, updated = 'all') {
        if (updated === 'all' || updated === 'featureOrder') {
            const movedIndex = this.featureOrder.getMovedFeatureIndex(node);
            this.featureOrder.updateFeatureOrder(node);
            if (movedIndex !== undefined && movedIndex >= 0) {
                this.featureOrder.recursivelyUpdateFeatureVisibility([node.features[movedIndex]], node.features, []);
            }
        }

        this.updateQueue.set(node._dynamicId, node);
    }

    updateBaseGeometry(node: MeshNode, vertexData: SubmeshVertexData) {
        this.baseGeometryMap.set(node._dynamicId, vertexData);
        const geometry = vertexData.toTransferrable();
        this.worker.sendData(
            'renderUpdateBaseGeometry',
            { meshDynamicId: node._dynamicId, vertexData: geometry.vertexData },
            geometry.transfer
        );
    }

    /** This async function will not complete until ALL features on all meshes are done procesing */
    async processUpdateQueue(force = false) {
        // If the workers are busy processing already, any queued updates will be processed after the current batch anyways
        // So we can just return here
        if (this._workersBusy && !force) {
            return;
        }

        this._workersBusy = true;
        this._busy.next(true);
        const promises = [];
        for (const node of this.updateQueue.values()) {
            this.updateQueue.delete(node._dynamicId);

            if (isCustomMeshNode(node) && !node.geometryLoaded) {
                continue;
            }
            const updatePromise = this.processFeatures(node);
            if (!updatePromise) {
                continue;
            }
            promises.push(updatePromise);
        }

        await Promise.all(promises);

        // check if the queue is empty
        if (this.updateQueue.size > 0) {
            await this.processUpdateQueue(true);
        }

        this._workersBusy = false;
        this._busy.next(false);
    }

    protected async processFeatures(node: MeshNode) {
        const manager = node._manager;
        if (!manager) {
            return;
        }

        const featuresTransfer: ArrayBuffer[] = [];
        const featuresPayload: Array<IWorkerRenderFeaturesDataPayload<any>> = [];
        for (const feature of node.features) {
            const renderer = this.renderers.get(Token.withKey(feature.$type));
            if (!renderer) {
                throw Error('no renderer for feature');
            }

            const featurePayload = await renderer.makeWorkerPayload(feature, this.processedGeometryMap);
            if (featurePayload.transfer) {
                featuresTransfer.push(...featurePayload.transfer);
                delete featurePayload.transfer;
            }
            featuresPayload.push({
                ...featurePayload,
                dynamicId: feature._dynamicId,
                suppressed: feature._suppressed,
            });
        }

        const payload: IWorkerRenderFeaturesPayload = {
            meshDynamicId: node?._dynamicId,
            features: featuresPayload,
        };

        const result = await firstValueFrom(this.worker.startTask('renderFeatures', payload, featuresTransfer)).catch(
            (e: IWorkerRenderUpdateGeometryError) => {
                if (e.code !== 'NO_GEOMETRY') {
                    console.error(`error processing features on node ${node.name}`, e);
                }
                return null;
            }
        );
        if (!result) {
            return;
        }
        const resultGeometry = result.payload;
        if ('code' in resultGeometry) {
            if (resultGeometry.code !== 'NO_GEOMETRY') {
                console.error(`error processing features on node ${node.name}`, resultGeometry);
            }
            return null;
        }
        const vertexDataNew = SubmeshVertexData.CreateFromData(resultGeometry);
        this.processedGeometryMap.set(payload.meshDynamicId, vertexDataNew);

        if (this.debugMode) {
            this.compareGeometry(node, resultGeometry);
            return vertexDataNew;
        } else {
            return this.applyGeometry(node, vertexDataNew);
        }
    }

    protected applyGeometry(node: MeshNode, vertexDataNew: SubmeshVertexData) {
        let vertexDataCurrent = this.processedGeometryMap.get(node._dynamicId);
        if (!vertexDataCurrent) {
            vertexDataCurrent = this.baseGeometryMap.get(node._dynamicId);
        }
        if (!vertexDataCurrent) {
            throw Error('No vertex data found for mesh node');
        }

        if (vertexDataNew.submeshData) {
            if (!vertexDataCurrent.positions) {
                throw Error('Invalid geometry: positions is null');
            }
            const submeshData = vertexDataNew.submeshData.filter(s => s.materialIndex !== 0);
            const baseSubmeshes = calculateBaseSubmeshes(vertexDataCurrent, vertexDataNew, submeshData);
            const verticesCount = (vertexDataNew.positions || vertexDataCurrent.positions).length / 3;
            submeshData.unshift(
                ...baseSubmeshes.map(bs => ({
                    materialIndex: 0,
                    verticesStart: 0,
                    verticesCount: verticesCount,
                    indexStart: bs.indexStart,
                    indexCount: bs.indexCount,
                }))
            );
            vertexDataNew.submeshData = submeshData;
        }

        if (Object.getOwnPropertyNames(vertexDataNew).length > 0) {
            const mesh = this.viewer.getRendererNode(node) as Mesh;
            const updatedVertexData = vertexDataCurrent.shallowClone();
            SubmeshVertexData.ExtendToRef(vertexDataNew, updatedVertexData);

            const invalid = verifyGeometry(updatedVertexData);
            if (invalid) {
                console.error(
                    `Invalid or corrupted geometry output after feature processing in mesh ${node.name}. The ${invalid} are invalid.`
                );
            } else {
                updatedVertexData.applyToMesh(mesh);

                node._polygonCount = mesh.getTotalIndices() / 3;
                node._verticesCount = mesh.getTotalVertices();
                this.viewer.polygonTracker.next();
                this.viewer.warningTracker.next();
            }

            this.rendererManager.mesh.setRenderModeOnMesh(mesh, node._rootScene!);

            this.viewer.bbCache.invalidate(node);
            this.processedGeometryMap.set(node._dynamicId, updatedVertexData);

            // If the geometry was updated, run the post update functions on the feature processors
            for (let i = 0; i < node.features.length; i++) {
                const feature = node.features[i];

                const renderer = this.renderers.get(Token.withKey(feature.$type));
                if (!renderer) {
                    throw Error('no renderer for feature');
                }
                if (renderer.postUpdateGeometry) {
                    renderer.postUpdateGeometry(feature, updatedVertexData);
                }
            }

            return updatedVertexData;
        }
        return vertexDataCurrent;
    }

    protected compareGeometry(node: MeshNode, resultGeometry: ISubmeshVertexData) {
        const vertexDataCurrent = this.rendererManager.features.processedGeometryMap.get(node._dynamicId);
        if (!vertexDataCurrent) {
            console.error('No vertex data found for mesh node');
        } else {
            // compare the two geometries to make sure they match
            // if they don't match, output console error
            const properties: Array<keyof ISubmeshVertexData> = [
                'positions',
                'normals',
                'uvs',
                'colors',
                'indices',
                'uvs2',
                'uvs3',
                'uvs4',
                'uvs5',
                'uvs6',
                'paths',
                'holes',
                // NOTE: Exclude 'preserveNormals' and 'submeshData' - handled separately
            ];

            let mismatch: null | { idx: number; prop: keyof ISubmeshVertexData } = null;

            for (let i = 0; i < properties.length; i++) {
                const prop = properties[i];
                
                // Skip non-array properties
                if (prop === 'preserveNormals') {
                    continue;
                }
                
                if (prop === 'submeshData') {
                    const diffIdx = arrayDiff(resultGeometry[prop], vertexDataCurrent[prop], (a, b) => {
                        return (
                            a.indexCount === b.indexCount &&
                            a.indexStart === b.indexStart &&
                            a.verticesCount === b.verticesCount &&
                            a.verticesStart === b.verticesStart
                        );
                    });
                    if (diffIdx !== null) {
                        mismatch = { idx: diffIdx, prop };
                        break;
                    }
                    continue;
                }

                // Type guard: ensure we're only passing array-like properties
                const resultValue = resultGeometry[prop];
                const currentValue = vertexDataCurrent[prop];
                
                // Skip if either value is a boolean (safety check)
                if (typeof resultValue === 'boolean' || typeof currentValue === 'boolean') {
                    continue;
                }

                const diffIdx = arrayDiff(resultValue, currentValue);
                if (diffIdx !== null) {
                    mismatch = { idx: diffIdx, prop };
                    break;
                }
            }

            if (mismatch) {
                console.error(`geometry mismatch between worker and legacy feature renderers: ${node.name}`);
                console.error(mismatch);
                console.warn(resultGeometry, vertexDataCurrent);
            } else {
                // eslint-disable-next-line no-console
                console.log(`✅ geometry match: ${node.name}`);
            }

            const newGeometry = SubmeshVertexData.CreateFromData(resultGeometry);
            for (let i = 0; i < node.features.length; i++) {
                const feature = node.features[i];

                const renderer = this.renderers.get(Token.withKey(feature.$type));
                if (!renderer) {
                    throw Error('no renderer for feature');
                }
                if (renderer.postUpdateGeometry) {
                    renderer.postUpdateGeometry(feature, newGeometry);
                }
            }

            // Update geometry for any meshes that depend on this one
            const referencerFeatures = this.viewer.getReferencesToNode(node).filter(isFeatureNode);
            const meshesToUpdate = new Map<string, MeshNode>();
            for (const feature of referencerFeatures) {
                const meshParent = feature._parent;
                if (meshParent && isMeshNode(meshParent)) {
                    meshesToUpdate.set(meshParent._dynamicId, meshParent);
                }
            }

            for (const mesh of meshesToUpdate.values()) {
                this.queueMeshForUpdate(mesh);
            }
        }
    }

    delete3d(feature: ITrackedClass<Feature>, preserveCache?: boolean) {
        const meshNode = feature._parent;
        // Only re-queue the mesh for update if its 3D representation still exists.
        // If the mesh itself is being deleted in the same cycle, its Babylon node has
        // already been removed and re-queuing it would cause processFeatures to run
        // on a disposed mesh, resulting in an unhandled rejection that permanently
        // locks _workersBusy and freezes the loading spinner.
        if (meshNode && this.viewer.getRendererNode(meshNode)) {
            this.queueMeshForUpdate(meshNode);
        }

        const renderer = this.renderers.get(Token.withKey(feature.$type));
        if (!renderer) {
            throw Error('no renderer for feature');
        }
        renderer.delete3d(feature, preserveCache);
    }

    dispose() {
        this.worker.dispose();
        this.updateQueue.clear();
        this.renderers.forEach(renderer => renderer.dispose());
        super.dispose();
    }
}

type ArrayEl<T> = T extends Array<infer Item> ? Item : T extends Float32Array | IndicesArray ? number : never;

// Compare two arrays for equality
function arrayDiff<
    A extends any[] | Float32Array | IndicesArray | undefined | null,
    B extends any[] | Float32Array | IndicesArray | undefined | null
>(a: A, b: B, predicate?: (a: ArrayEl<A>, b: ArrayEl<B>) => boolean): null | number {
    if (!a && !b) {
        return null;
    }
    if (!a || !b) {
        return 0;
    }
    if (a.length !== b.length) return Math.min(a.length, b.length);
    for (let i = 0; i < a.length; i++) {
        const av = a[i];
        const bv = b[i];
        if (predicate) {
            if (!predicate(av, bv)) return i;
        }
        if (typeof av === 'number' && typeof bv === 'number') {
            if (Math.abs(av - bv) > 0.001) return i;
        } else {
            if (av !== bv) return i;
        }
    }
    return null;
}
